/**
 * Debug de permisos / “ver como”.
 * Solo correos en PERMISSION_DEBUG_EMAILS (sesión real).
 */

import {
  canAccessAnalisisPrecios,
  canAccessCargaMasivaLeads,
  canAccessClasificacionRuts,
  canAccessConsultaOtorgamientos,
  canAccessControlGestionMenu,
  canAccessCotizadorFactoring,
  canAccessGestionCampana,
  canAccessMarketingMenu,
  canAccessNoOperados,
  canAccessPanelComercial,
  canAccessSeguimientoCesionesFacturacion,
  canAccessSeguimientoEjecutivo,
  canAccessSimuladorMora,
  canAccessSolicitudesExcepcionMora,
  canAccessTasasMoraPreacordadas,
  canAccessVentaAnual,
  isAplicacionesUser,
  isComercialUser,
} from './gestionCampanaAccess'

export const PERMISSION_DEBUG_EMAILS = Object.freeze([
  'dvalenzuela@interfactor.cl',
  'dvalanzuela@interfactor.cl', // variante en login/BD
])

const LS_VIEW_AS = 'mgc_permission_view_as'

/** Roles típicos para simular menú sin elegir un usuario concreto. */
export const DEBUG_ROLE_PRESETS = Object.freeze([
  'Administrador',
  'Gerencia',
  'Subgerente',
  'Lider de equipo',
  'Ejecutivo',
  'Comercial',
  'Aplicaciones',
])

function emailNorm(user) {
  if (!user || typeof user !== 'object') return ''
  return String(
    user.email ||
      user.mail ||
      user.MAIL ||
      user.correo ||
      user.CORREO ||
      user.usuario ||
      user.username ||
      user.user ||
      '',
  )
    .trim()
    .toLowerCase()
}

function nombreNorm(user) {
  if (!user || typeof user !== 'object') return ''
  return String(user.nombre || user.NOMBRE || user.name || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

/** Nombres permitidos si el login no trae email (fallback). */
export const PERMISSION_DEBUG_NAMES = Object.freeze(['david valenzuela'])

/** ¿La sesión real puede usar el depurador? */
export function canUsePermissionDebug(realUser) {
  if (!realUser) return false
  const em = emailNorm(realUser)
  if (em && PERMISSION_DEBUG_EMAILS.some((e) => em === String(e).trim().toLowerCase())) {
    return true
  }
  // Algunos logins no incluyen email en userData; permitir por nombre conocido.
  const nom = nombreNorm(realUser)
  if (nom && PERMISSION_DEBUG_NAMES.some((n) => nom === String(n).trim().toLowerCase())) {
    return true
  }
  return false
}

function pick(obj, ...keys) {
  for (const k of keys) {
    const v = obj?.[k]
    if (v != null && String(v).trim() !== '') return v
  }
  return ''
}

/** Normaliza fila de listado-usuarios a shape de login. */
export function userFromListRow(row) {
  if (!row || typeof row !== 'object') return null
  const email = String(pick(row, 'MAIL', 'mail', 'email')).trim()
  if (!email) return null
  const idRaw = pick(row, 'ID', 'id', 'ID_USUARIO', 'id_usuario')
  const idNum = idRaw === '' ? null : Number(idRaw)
  return {
    id: Number.isFinite(idNum) ? idNum : idRaw || null,
    email,
    mail: email,
    nombre: String(pick(row, 'NOMBRE', 'nombre') || email),
    tipo_usuario: String(pick(row, 'TIPO_USUARIO', 'tipo_usuario') || ''),
    grupo: String(pick(row, 'GRUPO', 'grupo') || ''),
    sucursal: String(pick(row, 'SUCURSAL', 'sucursal') || ''),
    ejec_sist_factoring: String(
      pick(row, 'EJEC_SIST_FACTORING', 'ejec_sist_factoring', 'ejec_sis_fact') || '',
    ),
    tipo_ejecutivo: String(pick(row, 'TIPO_EJECUTIVO', 'tipo_ejecutivo') || ''),
  }
}

/**
 * Usuario sintético por rol: sin email/id reales para no heredar whitelists
 * del depurador (p. ej. NO_OPERADOS_EMAILS).
 */
export function buildRoleSimulationUser(tipoUsuario) {
  const tipo = String(tipoUsuario || '').trim() || 'Ejecutivo'
  return {
    id: null,
    email: '',
    mail: '',
    nombre: `[Simulación] ${tipo}`,
    tipo_usuario: tipo,
    grupo: '',
    sucursal: '',
    ejec_sist_factoring: '',
    _viewAs: { mode: 'role', label: tipo },
  }
}

export function buildUserSimulationUser(targetUser) {
  if (!targetUser) return null
  return {
    ...targetUser,
    email: targetUser.email || targetUser.mail || '',
    mail: targetUser.mail || targetUser.email || '',
    _viewAs: {
      mode: 'user',
      label: targetUser.nombre || targetUser.email || 'Usuario',
    },
  }
}

export function readViewAsFromStorage() {
  try {
    const raw = sessionStorage.getItem(LS_VIEW_AS)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !parsed.user) return null
    return parsed
  } catch {
    return null
  }
}

export function writeViewAsToStorage(payload) {
  try {
    if (!payload) {
      sessionStorage.removeItem(LS_VIEW_AS)
      return
    }
    sessionStorage.setItem(LS_VIEW_AS, JSON.stringify(payload))
  } catch {
    /* ignore */
  }
}

export function clearViewAsStorage() {
  writeViewAsToStorage(null)
}

/**
 * Catálogo de pantallas/módulos con control de acceso en frontend.
 * `check` refleja menú + guards (Require*).
 */
export const PERMISSION_SCREENS = Object.freeze([
  {
    id: 'inicio',
    label: 'Inicio',
    path: '/inicio',
    group: 'General',
    check: (u) => !isAplicacionesUser(u) && !isComercialUser(u),
  },
  {
    id: 'cartera',
    label: 'Cartera / Tubo / Campañas',
    path: '/cartera-ejecutivo',
    group: 'Cartera',
    check: (u) => !isAplicacionesUser(u) && !isComercialUser(u),
  },
  {
    id: 'potenciales',
    label: 'Potenciales (En gestión / Perdidos)',
    path: '/en-gestion',
    group: 'Potenciales',
    check: (u) => !isAplicacionesUser(u) && !isComercialUser(u),
  },
  {
    id: 'carga-masiva',
    label: 'Carga masiva leads',
    path: '/carga-masiva-leads',
    group: 'Potenciales',
    check: (u) => canAccessCargaMasivaLeads(u),
  },
  {
    id: 'seguimiento-com',
    label: 'Seguimiento comercial (Factoring/Crédito/Leasing)',
    path: '/factoring',
    group: 'Seguimiento',
    check: (u) => !isAplicacionesUser(u) && !isComercialUser(u),
  },
  {
    id: 'simulador-ops',
    label: 'Simulador de operaciones',
    path: '/simulador-operaciones',
    group: 'Herramientas',
    check: (u) => !isAplicacionesUser(u) && !isComercialUser(u),
  },
  {
    id: 'simulador-mora',
    label: 'Simulador Mora y Excepción',
    path: '/simulador-mora-excepcion',
    group: 'Herramientas',
    check: (u) => canAccessSimuladorMora(u),
  },
  {
    id: 'solicitudes-mora',
    label: 'Solicitudes excepción mora',
    path: '/solicitudes-excepcion-mora',
    group: 'Herramientas',
    check: (u) => canAccessSolicitudesExcepcionMora(u) && !isComercialUser(u),
  },
  {
    id: 'tasas-mora',
    label: 'Tasas mora preacordadas',
    path: '/tasas-mora-preacordadas',
    group: 'Herramientas',
    check: (u) => canAccessTasasMoraPreacordadas(u) && !isComercialUser(u),
  },
  {
    id: 'consulta-oto',
    label: 'Consulta otorgamientos',
    path: '/consulta-otorgamientos',
    group: 'Herramientas',
    check: (u) =>
      !isAplicacionesUser(u) && !isComercialUser(u) && canAccessConsultaOtorgamientos(u),
  },
  {
    id: 'analisis-precios',
    label: 'Análisis de precios',
    path: '/analisis-precios',
    group: 'Herramientas',
    check: (u) =>
      !isAplicacionesUser(u) && !isComercialUser(u) && canAccessAnalisisPrecios(u),
  },
  {
    id: 'seguimiento-cesiones',
    label: 'Seguimiento cesiones/facturación',
    path: '/seguimiento-cesiones-facturacion',
    group: 'Herramientas',
    check: (u) =>
      !isAplicacionesUser(u) &&
      !isComercialUser(u) &&
      canAccessSeguimientoCesionesFacturacion(u),
  },
  {
    id: 'panel-comercial',
    label: 'Panel Comercial',
    path: '/panel-comercial',
    group: 'Herramientas',
    check: (u) => canAccessPanelComercial(u),
  },
  {
    id: 'panel-gerencial',
    label: 'Panel Gerencial',
    path: '/panel-gerencial',
    group: 'Herramientas',
    check: (u) => canAccessPanelComercial(u),
  },
  {
    id: 'cotizador',
    label: 'Cotizador factoring',
    path: '/cotizador-factoring',
    group: 'Herramientas',
    check: (u) => canAccessCotizadorFactoring(u),
  },
  {
    id: 'menu-campana',
    label: 'Menú Gestión Campaña',
    path: '/panel-marketing/carga-campana',
    group: 'Gestión Campaña',
    check: (u) =>
      !isAplicacionesUser(u) && !isComercialUser(u) && canAccessMarketingMenu(u),
  },
  {
    id: 'gestion-campana',
    label: 'Gestión Campaña (admin / extras)',
    path: '/panel-marketing/carga-campana',
    group: 'Gestión Campaña',
    check: (u) => canAccessGestionCampana(u),
  },
  {
    id: 'seguimiento-ejec',
    label: 'Seguimiento ejecutivos',
    path: '/panel-marketing/seguimiento-ejecutivo',
    group: 'Gestión Campaña',
    check: (u) => canAccessSeguimientoEjecutivo(u),
  },
  {
    id: 'no-operados',
    label: 'Clientes No Operados',
    path: '/panel-marketing/no-operados',
    group: 'Gestión Campaña',
    check: (u) => canAccessNoOperados(u),
  },
  {
    id: 'control-gestion',
    label: 'Control de gestión',
    path: '/control-gestion/clasificacion-ruts',
    group: 'Control de gestión',
    check: (u) =>
      !isAplicacionesUser(u) && !isComercialUser(u) && canAccessControlGestionMenu(u),
  },
  {
    id: 'clasificacion-ruts',
    label: 'Clasificación RUTs',
    path: '/control-gestion/clasificacion-ruts',
    group: 'Control de gestión',
    check: (u) => canAccessClasificacionRuts(u),
  },
  {
    id: 'venta-anual',
    label: 'Venta anual',
    path: '/control-gestion/venta-anual',
    group: 'Control de gestión',
    check: (u) => canAccessVentaAnual(u),
  },
  {
    id: 'admin-seguimiento',
    label: 'Generar Seguimiento (admin)',
    path: '/crear-reporte-diario',
    group: 'Administración',
    check: (u) => String(u?.tipo_usuario || '') === 'Administrador',
  },
  {
    id: 'admin-usuarios',
    label: 'Usuarios / Metas / Sync',
    path: '/usuarios/gestion',
    group: 'Administración',
    check: (u) => String(u?.tipo_usuario || '') === 'Administrador',
  },
])

export function evaluateScreens(user) {
  return PERMISSION_SCREENS.map((screen) => ({
    ...screen,
    allowed: Boolean(screen.check(user)),
  }))
}
